"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import Logo from "./Logo";
import { usePartnerStudentBranding } from "@/hooks/usePartnerStudentBranding";

export default function Footer() {
  const pathname = usePathname() ?? "";
  const hideForPartnerLanding =
    pathname.startsWith("/start/") || pathname.startsWith("/partner/student/");

  const fetchPartnerBranding =
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/assessment") ||
    pathname.startsWith("/onboarding") ||
    pathname.startsWith("/report/");

  const partnerCoBrand = usePartnerStudentBranding(fetchPartnerBranding && !hideForPartnerLanding);

  if (hideForPartnerLanding) return null;
  if (partnerCoBrand?.active) return null;

  const currentYear = new Date().getFullYear();

  return (
    <footer className="mt-auto border-t border-slate-800/80 bg-slate-950 text-slate-300">
      <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:py-12">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Logo href="/" className="[&_img]:h-9 [&_span]:text-lg" />
            <p className="mt-2 text-xs leading-relaxed text-slate-400">
              Premium AI career guidance for India—five-layer assessment, career dialogue, and a structured report for clarity and direction.
            </p>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Quick links
            </h3>
            <ul className="mt-3 space-y-2">
              <li>
                <Link href="/" className="text-sm hover:text-amber-300">
                  Home
                </Link>
              </li>
              <li>
                <Link href="/how-it-works" className="text-sm hover:text-amber-300">
                  How it works
                </Link>
              </li>
              <li>
                <Link href="/#pricing" className="text-sm hover:text-amber-300">
                  Pricing
                </Link>
              </li>
              <li>
                <Link href="/#faq" className="text-sm hover:text-amber-300">
                  FAQ
                </Link>
              </li>
              <li>
                <Link href="/login" className="text-sm hover:text-amber-300">
                  Sign in
                </Link>
              </li>
              <li>
                <Link href="/signup" className="text-sm hover:text-amber-300">
                  Sign up
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal & support */}
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
              Legal & support
            </h3>
            <ul className="mt-3 space-y-2">
              <li>
                <Link href="/terms" className="text-sm hover:text-amber-300">
                  Terms of use
                </Link>
              </li>
              <li>
                <Link href="/privacy" className="text-sm hover:text-amber-300">
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link href="/contact" className="text-sm hover:text-amber-300">
                  Contact us
                </Link>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 border-t border-slate-800/80 pt-6 lg:mt-12">
          <p className="text-center text-xs text-slate-500">
            © {currentYear} Aptinomic. All rights reserved.
          </p>
          <p className="mt-2 text-center text-xs text-slate-500">
            A product of{" "}
            <a
              href="https://www.altivistech.in"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-slate-400 underline decoration-slate-600 underline-offset-2 hover:text-amber-300 hover:decoration-amber-400"
            >
              ALTIVIS TECHNOLOGIES
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
